import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";

/**
 * workaround react-hook-form issue
 */
export function useCustomField<T>(fieldId: string, defaultValue: T) {
	const { setValue, clearErrors, watch, getValues } = useFormContext();
	const currentValue = watch(fieldId);

	const originalValueRef = useRef<T | null>(getValues(fieldId));
	const wasOriginallyChecked =
		originalValueRef.current !== null && originalValueRef.current !== undefined;

	const [isChecked, setChecked] = useState(!!currentValue);
	const [lastState, setLastState] = useState<T | null>(
		currentValue ?? defaultValue
	);

	const [nextAction, setNextAction] = useState<"idle" | "setNull" | "clear">(
		"idle"
	);

	useEffect(() => {
		if (nextAction === "setNull") {
			setValue(fieldId, null, {
				shouldValidate: true,
				shouldDirty: wasOriginallyChecked,
			});

			// If the original was null and we're setting to null, we need to check
			// if any other field is now different to determine dirty state
			if (!wasOriginallyChecked) {
				setValue("__lastModified" as any, Date.now(), { shouldDirty: true });
			}
			setChecked(false);
			setNextAction("clear");
		}
	}, [nextAction, setValue, fieldId, wasOriginallyChecked]);

	useEffect(() => {
		if (nextAction === "clear") {
			clearErrors(fieldId);
			setNextAction("idle");
		}
	}, [nextAction, clearErrors, fieldId]);

	const clearField = () => {
		const current = getValues(fieldId);
		if (current !== null && current !== undefined) {
			setLastState(current);
		}
		setChecked(false);
		setNextAction("setNull");
	};

	const checkField = () => {
		const valueToSet = lastState ?? defaultValue;
		setValue(fieldId, valueToSet, {
			shouldValidate: true,
			shouldDirty: true,
		});

		if (!wasOriginallyChecked) {
			setValue("__lastModified" as any, Date.now(), { shouldDirty: true });
		}

		setChecked(true);
	};

	const setFieldChecked = (value: boolean) => {
		if (value) {
			checkField();
		} else {
			clearField();
		}
	};

	return { clearField, checkField, isChecked, setFieldChecked };
}
